import { createFeatureSelector, createSelector } from '@ngrx/store';
import { KnowledgeBaseState } from './knowledge-base.reducers';
import { IKbDocument, IKbStats, IKbTypeFilter, KbDocumentStatus, KbDocumentType } from '../models/knowledge-base.models';

// ── Feature selector ─────────────────────────────────────────────────────────

export const selectKnowledgeBase = createFeatureSelector<KnowledgeBaseState>('knowledgeBase');

// ── Basic selectors ──────────────────────────────────────────────────────────

export const selectAllDocuments = createSelector(selectKnowledgeBase, (state) => state.documents);
export const selectActiveTypeFilter = createSelector(selectKnowledgeBase, (state) => state.activeTypeFilter);
export const selectSearchQuery = createSelector(selectKnowledgeBase, (state) => state.searchQuery);
export const selectSortKey = createSelector(selectKnowledgeBase, (state) => state.sortKey);
export const selectSortMode = createSelector(selectKnowledgeBase, (state) => state.sortMode);
export const selectExpandedDocumentIds = createSelector(selectKnowledgeBase, (state) => state.expandedDocumentIds);
export const selectLoading = createSelector(selectKnowledgeBase, (state) => state.loading);

// ── Derived selectors ────────────────────────────────────────────────────────

/** Map template sort keys to IKbDocument field accessors. */
const SORT_KEY_MAP: Record<string, (doc: IKbDocument) => string> = {
  name: (doc) => doc.fileName.toLowerCase(),
  type: (doc) => doc.type,
  status: (doc) => doc.status,
  upload_date: (doc) => doc.uploadDate ?? '',
};

/** Documents filtered by active type/search, then sorted. */
export const selectFilteredDocuments = createSelector(
  selectAllDocuments,
  selectActiveTypeFilter,
  selectSearchQuery,
  selectSortKey,
  selectSortMode,
  (documents, typeFilter, searchQuery, sortKey, sortMode) => {
    let filtered = documents;

    if (typeFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.type === typeFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((doc) => doc.fileName.toLowerCase().includes(query));
    }

    const accessor = SORT_KEY_MAP[sortKey];
    if (accessor) {
      const dir = sortMode === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => accessor(a).localeCompare(accessor(b)) * dir);
    }

    return filtered;
  },
);

/** Type filter tabs with counts. */
export const selectTypeFilters = createSelector(selectAllDocuments, (documents): IKbTypeFilter[] => {
  const typeCounts = documents.reduce(
    (acc, doc) => {
      acc[doc.type] = (acc[doc.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const orderedTypes: KbDocumentType[] = [
    'general',
    'product_catalog',
    'price_list',
    'customer_profile',
    'contract',
    'shipping_policy',
    'return_policy',
    'technical_specifications',
    'marketing_material',
    'order_history',
    'inventory',
  ];
  const availableTypes = orderedTypes.filter((type) => !!typeCounts[type]);
  const types: (KbDocumentType | 'all')[] = ['all', ...availableTypes];

  return types.map((type) => ({
    type,
    labelKey: `knowledge_base.filter_type.${type}`,
    count: type === 'all' ? documents.length : typeCounts[type] || 0,
  }));
});

/** Summary stats derived reactively from the current document list. */
export const selectStats = createSelector(selectAllDocuments, (documents): IKbStats => {
  const uploadDates = documents
    .map((doc) => doc.uploadDate)
    .filter((date): date is string => !!date)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  return {
    indexedCount: documents.filter((doc) => doc.status === KbDocumentStatus.INDEXED).length,
    lastUploadDate: uploadDates[0] ?? null,
  };
});

/** Check if a specific document row is expanded. */
export const selectIsDocumentExpanded = (documentId: string) => createSelector(selectExpandedDocumentIds, (expandedIds) => !!expandedIds[documentId]);
